import { PORTFOLIO_ASSET_ALLOCATION } from '@/enums/AssetAllocation';
import AppRegion from '@/types/app-region';
import BasicBenchmark from '@/types/basic-benchmark';
import Benchmark from '@/types/benchmark';
import InstrumentPerformance, { NoBenchmarkBasedMetrics } from '@/types/instruments/instrument-performance';
import LocalizedAllocationDetails from '@/types/localized-allocation-details';
import LocalizedString from '@/types/localized-string';
import { calculatePerformanceCommonInception } from '@/utils/calculation-utils';
import { LOCALE } from '@/utils/i18n';

/**
 * Serialize a benchmark to Basic Benchmark (useful for reducing payloads and storing info on the comparison item)
 * @param {Benchmark} benchmark the benchmark to serialize
 * @returns {BasicBenchmark} the serialized benchmark
 */
export const benchmarkToBasicBenchmark = (benchmark: Benchmark): BasicBenchmark => ({
  names: {
    ...benchmark.names,
  },
  performance: {
    standardDeviation: benchmark.performance.standardDeviation,
    percentGrowth: benchmark.performance.percentGrowth,
  },
  lipperId: benchmark.lipperId as string,
});

/**
 * Gets the formatted asset allocations from a fund model object.
 *
 * @param {Object} allocationsData Allocations data from the fund model.
 * @return {Object} Formatted asset allocations.
 */
export const getAssetAllocations = (allocationsData: LocalizedAllocationDetails[], appRegion: AppRegion) => {
  const defaultAllocations = {
    [PORTFOLIO_ASSET_ALLOCATION.EQ]: 0,
    [PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ]: 0,
    [PORTFOLIO_ASSET_ALLOCATION.ALTERNATIVE_INVESTMENT]: 0,
    [PORTFOLIO_ASSET_ALLOCATION.US_EQ]: 0,
    [PORTFOLIO_ASSET_ALLOCATION.FOREIGN_EQ]: 0,
    [PORTFOLIO_ASSET_ALLOCATION.FIXED_INCOME]: 0,
    [PORTFOLIO_ASSET_ALLOCATION.CASH]: 0,
    [PORTFOLIO_ASSET_ALLOCATION.OTHER]: 0,
  };

  const allocations: Record<PORTFOLIO_ASSET_ALLOCATION, number> = Object.values(PORTFOLIO_ASSET_ALLOCATION).reduce((acc, allocation) => {
    const assets = allocationsData.filter((a: { name: LocalizedString; weight: number }) => {
      const name = a.name[LOCALE.EN_CA];
      return name && name.includes(allocation);
    }, {});

    acc[allocation] = assets.reduce((acc2, asset) => acc2 + asset.weight, 0);
    return acc;
  }, defaultAllocations);

  if (appRegion !== AppRegion.CAN) {
    // these values where set 6 lines above, shut up TypeScript.
    allocations[PORTFOLIO_ASSET_ALLOCATION.FOREIGN_EQ]! += allocations[PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ]!;
    allocations[PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ] = 0;
  }

  if (allocations[PORTFOLIO_ASSET_ALLOCATION.EQ] === 0) {
    allocations[PORTFOLIO_ASSET_ALLOCATION.EQ] +=
      allocations[PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ] + allocations[PORTFOLIO_ASSET_ALLOCATION.US_EQ] + allocations[PORTFOLIO_ASSET_ALLOCATION.FOREIGN_EQ];
  }

  return allocations;
};

/**
 * Adds common inception performance metrics to an instrument's performance data.
 * Common inception represents the performance period starting from the most recently created
 * instrument in a comparison set.
 *
 * @param {InstrumentPerformance | AltPerformance} performance - The original performance data of the instrument
 *                                                               Contains rolling returns and growth metrics
 * @param {number} [startTimestamp] - Optional Unix timestamp marking the start of common inception period
 *                                   If not provided, original performance data is returned unchanged
 *
 * @returns {InstrumentPerformance | AltPerformance} Performance data with added common inception (CI) metrics
 *                                                   in the percentGrowth object
 *
 * @example
 * // Add common inception metrics to stock performance
 * const updatedPerformance = addCommonInceptionToPerformance(
 *   stockPerformance,
 *   comparison.commonInception.startTimestamp
 * );
 *
 * // Use with alternative investment performance
 * const altPerformance = addCommonInceptionToPerformance(
 *   altInvestmentPerformance,
 *   comparison.commonInception.startTimestamp
 * );
 */
export const addCommonInceptionToPerformance = (performance: InstrumentPerformance | NoBenchmarkBasedMetrics, startTimestamp?: number): InstrumentPerformance =>
  ({
    ...performance,
    percentGrowth: {
      ...performance?.percentGrowth,
      CI: calculatePerformanceCommonInception(performance?.rolling?.monthly || [], startTimestamp),
    },
  }) as InstrumentPerformance;
